import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ContentUpgrades } from "@swizec/gatsby-theme-course-platform";
export const _frontmatter = {
  "title": "How to make what you're worth even if you're from the wrong country",
  "description": "",
  "published": "2019-05-13T00:00:00.000Z",
  "redirect_from": ["/blog/how-to-make-what-youre-worth-when-youre-from-the-wrong-country/swizec/9076"],
  "categories": "Business, Freelancing",
  "content_upgrade": "SeniorMindset",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`It always upsets me when I see talented engineers bickering about whether they're worth $12/hour or $15/hour. Happens a lot in Slovenian Facebook groups and I'm sure many others.`}</p>
    <p>{`I'm from Slovenia by the way, some don't know. 🤷‍♂️`}</p>
    <p>{`When discussions about salary come up, the group splits in two:`}</p>
    <ol>
      <li parentName="ol">{`Those with US or EU salaries saying it's nigh impossible for an able-minded engineer to stay under $80,000/year 2) Those with local salaries, in the $30,000/year range, saying everybody else is a liar`}</li>
    </ol>
    <p>{`Silicon valley $300,000/year salaries are summarily dismissed as impossible and unachievable so they aren't part of the conversation.`}</p>
    <p>{`Discussion quickly devolves into name calling, excuses, and my favorite `}<em parentName="p">{`"Yeah well I'm not greedy like you, I live a good life, feel rich in my environment, and provide for the kids. Why should I want more"`}</em></p>
    <p>{`Because money is nice ...`}</p>
    <ContentUpgrades.SeniorMindset />
    <p>{`Well, debatable. Let's assume that money is nice, solves most problems, and that lacking money is stressful. You can cushion against lacking money by making more than you need.`}</p>
    <p>{`If you truly are happy and satisfied with your income, you can stop reading now. You win at life. Congratz 👏`}</p>
    <p>{`For everybody else: Here's some ways you can make more even if you're from the wrong country.`}</p>
    <p>{`Without resorting to`}</p>
    <ul>
      <li parentName="ul">{`yeah but you have a blog`}</li>
      <li parentName="ul">{`yeah but you got lucky`}</li>
      <li parentName="ul">{`yeah but you have a podcast`}</li>
      <li parentName="ul">{`yeah but you do open source`}</li>
      <li parentName="ul">{`yeah but you have more time`}</li>
      <li parentName="ul">{`yeah but you go to conferences`}</li>
      <li parentName="ul">{`yeah but you ...`}</li>
    </ul>
    <p>{`Yeah but everyone was born pretty much the same. Without a blog or podcast or even engineering skills. Now look at us, we're all writing code and solving business problems like there's no tomorrow.`}</p>
    <p>{`And we've all got the same 24 hours in the day.`}</p>
    <p>{`The difference is in the mind.`}</p>
    <p><img parentName="p" {...{
        "src": "/8939e4fc04b35c2173741bcf98edb3e4/media-d3mlE7uhX8KFgEmY-giphy.gif",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "how-your-environment-holds-you-back"
    }}>{`How your environment holds you back`}</h2>
    <blockquote>
      <p parentName="blockquote">{`You are the average of the five people you spend the most time with.`}</p>
    </blockquote>
    <p>{`You've heard that one before yeah? You are the average of the five people you spend most time with.`}</p>
    <p>{`Makes sense.`}</p>
    <p>{`Except it's even worse. You are the average of `}<em parentName="p">{`everyone`}</em>{` around you `}<a parentName="p" {...{
        "href": "https://medium.com/the-mission/youre-not-the-average-of-the-five-people-you-surround-yourself-with-f21b817f6e69"
      }}>{`[`}{`1`}{`]`}</a>{`. Not just five.`}</p>
    <p>{`Here's how that plays out in salaries 👇`}</p>
    <p><img parentName="p" {...{
        "src": "/1c6afc6f64616518fd260da10af57f08/media-l1KsvHJCZQyJHsynS-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`You live in a small town or country. You are surrounded by people making, say, $1960/month. The net purchase parity adjusted median income in Slovenia. `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/List_of_European_countries_by_average_wage#Net_average_monthly_salary_(adjusted_for_living_costs_in_PPP)"
      }}>{`[`}{`2`}{`]`}</a></p>
    <p>{`With $1960/month you can live a normal life, support a family when both parents work, send everyone to college (because in Europe it's free), go on holiday once a year, and drive a normal 5 year old car. You're not rich and you're not poor.`}</p>
    <p>{`Nothing wrong with that. Sounds like a great life.`}</p>
    <p>{`But you're a software engineer and software engineers are in high demand. Employers fight tooth and nail to work with you.`}</p>
    <p>{`You make $3000/month and you are king (or queen).`}</p>
    <p>{`Richest kid on the block. Best car, nicest clothes, shiny new iPhone. You rock.`}</p>
    <p>{`When others complain about their income at the pub, you stay quiet. Avert your eyes. Don't attract attention.`}</p>
    <p>{`What, just because you can afford a new iPhone you think you're better than us? Lemme tell you something, Joe, you're buying the next round.`}</p>
    <p>{`Sound familiar?`}</p>
    <p>{`You make so much it's almost embarrassing. Your job ain't hard, you don't come home dirty, your hands don't bleed, you sit in the office and play with computers. Just like you used to when computers were for video games.`}</p>
    <p>{`And so you don't feel making more is fair. Your environment says you've already got more than you need.`}</p>
    <p>{`Less `}<em parentName="p">{`"Wow I wonder how she got that BMW"`}</em>{`, more `}<em parentName="p">{`"Wow look at that asshole in her fancy car"`}</em></p>
    <p>{`Also known as `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Tall_poppy_syndrome"
      }}>{`the tall poppy syndrome`}</a></p>
    <h2 {...{
      "id": "1--change-your-environment"
    }}>{`1 👉 Change your environment`}</h2>
    <p>{`For comparison, a typical engineer's purchasing power adjusted salary in San Francisco is around $5800/month. Based on this `}<a parentName="p" {...{
        "href": "https://data.oecd.org/conversion/purchasing-power-parities-ppp.htm"
      }}>{`ppp calculator`}</a>{`, this `}<a parentName="p" {...{
        "href": "https://fred.stlouisfed.org/series/RPPALL41860"
      }}>{`regional parity index`}</a>{` saying SF is 1.24x more expensive than "normal USA", `}<a parentName="p" {...{
        "href": "https://smartasset.com/taxes/california-paycheck-calculator"
      }}>{`a CA salary calculator`}</a>{`, and my rudimentary math skills.`}</p>
    <p>{`So even though everyone tells you SF is too expensive ... a typical engineer still makes 4x `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Median_income"
      }}>{`median income`}</a>{`. 😇`}</p>
    <p><strong parentName="p">{`But you don't have to move!`}</strong></p>
    <p>{`You don't have to move to change your environment. This is 2019 we're talking about and your `}<em parentName="p">{`mental environment`}</em>{` trumps your physical environment.`}</p>
    <p>{`Go online. Surround yourself with people who have a positive attitude towards money.`}</p>
    <p>{`Associate with those who say `}<em parentName="p">{`"What can we do to make more?"`}</em>{` not those who say `}<em parentName="p">{`"The system is rigged and we're stuck"`}</em></p>
    <p>{`Hang out with ambitious folk. It's gonna rub off on you.`}</p>
    <p>{`Become friends with those who push you to strive not those who pull you back.`}</p>
    <p>{`Read books that normalize success. Avoid books that call successful people crooks.`}</p>
    <p>{`You can still move physically, if you want to, but that's harder than changing where you hang out online. Avoid places with a lot of victims complaining how life is unfair.`}</p>
    <h2 {...{
      "id": "2--upgrade-your-attitude"
    }}>{`2 👉 Upgrade your attitude`}</h2>
    <p>{`Once you upgrade your environment, it's time to work on your attitude. I don't have a shortcut for you here.`}</p>
    <p>{`You are worth more.`}</p>
    <p>{`You can make more.`}</p>
    <p>{`You can achieve things.`}</p>
    <p>{`But until `}<em parentName="p">{`you`}</em>{` believe that, nothing anyone can do to help. Hanging out with folks who push you to become the best you that you can be helps. Great first step :)`}</p>
    <p>{`And return the favor yeah? Encourage those around you.`}</p>
    <h2 {...{
      "id": "25--think-about-value"
    }}>{`2.5 👉 Think about value`}</h2>
    <p>{`The biggest part of upgrading your attitude is to think about value. Many words exist online about value-based pricing so I won't go too deep.`}</p>
    <p>{`The idea is this 👇`}</p>
    <p>{`Don't think about how much time or effort it takes to do something. Consider instead how much value it provides.`}</p>
    <lite-youtube {...{
      "videoid": "k5wayr6tYzY",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Work for a big company with a lot of revenue and any contribution you make, even if hard to measure, can have business impact measured in the millions.`}</p>
    <p>{`Work for a company that's struggling and it doesn't matter how good you are. The best skills in the world just aren't worth $50 to a business barely making $10.`}</p>
    <p>{`Everything changes when you think about income as return for value.`}</p>
    <h2 {...{
      "id": "3--income-arbitrage"
    }}>{`3 👉 Income arbitrage`}</h2>
    <p>{`Now the fun stuff: How you can make more without moving somewhere expensive.`}</p>
    <p>{`This is the approach a lot of professionals take. Live in a cheap country, work for a rich country. You don't even have to be a digital nomad.`}</p>
    <p>{`All you need is a remote job.`}</p>
    <p>{`How do you find a remote job? You're gonna have to hustle a little.`}</p>
    <p>{`First you gotta find someone looking for your skillset in a remote employee. `}<a parentName="p" {...{
        "href": "https://remoteok.io/"
      }}>{`RemoteOK`}</a>{` is a great job board with remote positions, `}<a parentName="p" {...{
        "href": "https://news.ycombinator.com"
      }}>{`HackerNews`}</a>{` has a "Who's hiring" thread every 1st of the month with hundreds of posts, you can try something like `}<a parentName="p" {...{
        "href": "https://www.toptal.com/"
      }}>{`TopTal`}</a>{` or `}<a parentName="p" {...{
        "href": "https://www.upwork.com/"
      }}>{`Upwork`}</a>{`.`}</p>
    <p>{`Or just network with people from the first section above. Ask how you can help them succeed. You'll be surprised how many would love to hire you.`}</p>
    <p>{`The income arbitrage blueprint looks like this:`}</p>
    <ol>
      <li parentName="ol">{`Become freelancer (start solo company) 2) Find leads (job boards, hackernews, etc) 3) Email a bunch of people 4) Find a match 5) Agree on value and get to work`}</li>
    </ol>
    <p>{`You'll need soft skills for this one. Ability to motivate yourself, stay focused, work some sales skills, think about outreach, how to grow your business, etc.`}</p>
    <p>{`Keep making your clients happy and you should be fine.`}</p>
    <p>{`You often won't make as much as if you were local to the client for various reasons too long to explain, but you're gonna make plenty more than you would at home.`}</p>
    <p>{`Why?`}</p>
    <p>{`Because your remote customer is making more money than your local customer. Your same skills are worth more.`}</p>
    <p>{`Remember: It's easy to pay $50 to make $150 and impossible to pay $50 to make $10.`}</p>
    <h2 {...{
      "id": "4--increase-your-value"
    }}>{`4 👉 Increase your value`}</h2>
    <p>{`Now it's time to increase your value `}<em parentName="p">{`and`}</em>{` your perceived value.`}</p>
    <p>{`What looks good to a prospective client?`}</p>
    <p>{`They need to know you're a real person with real history and skills. Having a blog helps to an extent. Existing on the internet helps too. Anything you can point to that says you are a professional in your field and know what you're doing.`}</p>
    <p>{`Client testimonials trump all. Collect those.`}</p>
    <p>{`When you work with someone, ask for a referral. Ask for a testimonial. Ask them how you can do even better next time.`}</p>
    <p>{`A warm intro is worth 10 cold emails.`}</p>
    <p>{`What I'm talking about roughly falls under "authority". What can you do to show your authority on the subject? Do it.`}</p>
    <p>{`Every niche is different. Don't impress your friends and family, focus on impressing your clients.`}</p>
    <p>{`You can also invest in your skills.`}</p>
    <p>{`If everyone's looking for JavaScript, why are you working on COBOL? Unless you have access to a bunch of clients looking specifically for COBOL, then go ahead.`}</p>
    <p>{`Once more this is largely about `}<em parentName="p">{`"What can I do that provides value?"`}</em></p>
    <p>{`Ask your clients. Learn it. Do it.`}</p>
    <p>{`Often you can increase your value tenfold just by being dependable. Clients (and bosses) love it when you can handle things without continuous prodding from their end.`}</p>
    <h2 {...{
      "id": "you-got-dis-"
    }}>{`You got dis 🤘`}</h2>
    <p>{`Hope that helps. I wrote it because it pains me every time I see an engineer reading a menu right-to-left. ✌️`}</p>
    <p>{`Cheers, ~Swizec`}</p>
    <p>{`PS: reading the menu right-to-left is a habit many consider normal. Then you tell someone who grew up well-off and they give you this funny look. What does right-to-left mean? It means you read the price first :)`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      